import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';

// Stable reference for the empty case. `?? []` would hand useMemo a brand-new array on every
// render, which is what react-hooks/exhaustive-deps flags here.
const NO_HISTORY: ConnectionTestHistory[] = [];
import { ConnectionTestHistory, ConnectionType } from '../../types/connection';
import { connectionsApi } from '../../services/api/connections';
import { StatusIndicator, StatusType } from '../shared/StatusIndicators';
import { TestResultPanel } from './TestResultPanel';
// THUB-186: timezone-aware shared formatter (was new Date(x).toLocaleString()).
import { formatDateTime, activeTimezone } from '../../utils/formatDateTime';

interface TestHistoryTableProps {
  connectionId: string;
  connectionType?: ConnectionType;
  transferMethod?: string;
  limit?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export function TestHistoryTable({
  connectionId,
  connectionType = 'outbound',
  transferMethod,
  limit = 20,
  autoRefresh = false,
  refreshInterval = 30000
}: TestHistoryTableProps) {
  // THUB-299: was a useState trio plus TWO effects -- one calling historyQuery.refetch, one managing a
  // setInterval for auto-refresh. useQuery's refetchInterval expresses the polling directly, so
  // both effects and the manual interval teardown are gone.
  //
  // Keyed under queryKeys.connections.detail so a connection-scoped invalidation reaches it.
  const historyQuery = useQuery({
    queryKey: [...queryKeys.connections.detail(connectionId), 'testHistory'],
    queryFn: () => connectionsApi.getConnectionTestHistory(connectionId),
    refetchInterval: autoRefresh ? refreshInterval : false,
  });
  const history: ConnectionTestHistory[] = historyQuery.data ?? NO_HISTORY;
  const loading = historyQuery.isPending;
  // Preserve the original message contract: the error's own message, else a default.
  const error = historyQuery.error
    ? ((historyQuery.error as { message?: string }).message || 'Failed to load test history')
    : null;
  const [selectedTest, setSelectedTest] = useState<ConnectionTestHistory | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Filter
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failure'>('all');


  // Filter and paginate data
  const filteredHistory = useMemo(() => {
    if (statusFilter === 'all') return history;
    return history.filter(test => {
      if (statusFilter === 'success') return test.testResults.success;
      if (statusFilter === 'failure') return !test.testResults.success;
      return true;
    });
  }, [history, statusFilter]);

  const paginatedHistory = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredHistory.slice(start, start + pageSize);
  }, [filteredHistory, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredHistory.length / pageSize);

  const getStatusColor = (success: boolean): StatusType => {
    return success ? 'healthy' : 'error';
  };

  const getTestTypeLabel = (testType: string): string => {
    switch (testType) {
      case 'manual': return 'Manual';
      case 'scheduled': return 'Scheduled';
      case 'pre_creation': return 'Pre-creation';
      default: return testType;
    }
  };

  const getTriggeredByLabel = (triggeredBy: string): string => {
    switch (triggeredBy) {
      case 'user': return 'User';
      case 'system': return 'System';
      case 'schedule': return 'Scheduler';
      default: return triggeredBy;
    }
  };

  // Summary statistics
  const stats = useMemo(() => {
    const total = history.length;
    const successful = history.filter(h => h.testResults.success).length;
    const failed = total - successful;
    const successRate = total > 0 ? Math.round((successful / total) * 100) : 0;
    const avgResponseTime = history.length > 0
      ? Math.round(history.reduce((sum, h) => sum + (h.testResults.responseTime || 0), 0) / history.length)
      : 0;

    return { total, successful, failed, successRate, avgResponseTime };
  }, [history]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-10 text-neutral-500 dark:text-neutral-400">
        <div className="w-5 h-5 border-2 border-neutral-200 dark:border-neutral-700 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin" />
        <span>Loading test history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-danger-600 dark:text-danger-400 mb-4">{error}</p>
        <button
          onClick={() => historyQuery.refetch()}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg p-4">
      {/* Summary Stats */}
      <div className="flex flex-wrap gap-4 mb-5">
        <div className="flex-1 min-w-[100px] p-3 bg-neutral-50 dark:bg-neutral-800 rounded-md text-center">
          <span className="block text-2xl font-semibold text-neutral-800 dark:text-neutral-200">{stats.total}</span>
          <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-1">Total Tests</span>
        </div>
        <div className="flex-1 min-w-[100px] p-3 bg-success-500/10 dark:bg-success-500/15 rounded-md text-center">
          <span className="block text-2xl font-semibold text-success-600 dark:text-success-400">{stats.successful}</span>
          <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-1">Successful</span>
        </div>
        <div className="flex-1 min-w-[100px] p-3 bg-danger-500/10 dark:bg-danger-500/15 rounded-md text-center">
          <span className="block text-2xl font-semibold text-danger-600 dark:text-danger-400">{stats.failed}</span>
          <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-1">Failed</span>
        </div>
        <div className="flex-1 min-w-[100px] p-3 bg-neutral-50 dark:bg-neutral-800 rounded-md text-center">
          <span className="block text-2xl font-semibold text-neutral-800 dark:text-neutral-200">{stats.successRate}%</span>
          <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-1">Success Rate</span>
        </div>
        <div className="flex-1 min-w-[100px] p-3 bg-neutral-50 dark:bg-neutral-800 rounded-md text-center">
          <span className="block text-2xl font-semibold text-neutral-800 dark:text-neutral-200">{stats.avgResponseTime}ms</span>
          <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-1">Avg Response</span>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="flex justify-between items-center mb-4 gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-neutral-600 dark:text-neutral-400">Filter by status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as typeof statusFilter);
              setCurrentPage(1);
            }}
            className="px-3 py-1.5 text-sm rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All</option>
            <option value="success">Successful</option>
            <option value="failure">Failed</option>
          </select>
        </div>
        <button
          onClick={() => historyQuery.refetch()}
          className="px-3 py-1.5 text-xs font-medium rounded-md bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* History Table */}
      {paginatedHistory.length === 0 ? (
        <div className="text-center py-10 text-neutral-500 dark:text-neutral-400">
          <p>No test history found.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-800">
                  <th className="text-left px-3 py-3 font-semibold text-neutral-700 dark:text-neutral-300 border-b-2 border-neutral-200 dark:border-neutral-700">Date/Time</th>
                  <th className="text-left px-3 py-3 font-semibold text-neutral-700 dark:text-neutral-300 border-b-2 border-neutral-200 dark:border-neutral-700">Status</th>
                  <th className="text-left px-3 py-3 font-semibold text-neutral-700 dark:text-neutral-300 border-b-2 border-neutral-200 dark:border-neutral-700">Response Time</th>
                  <th className="text-left px-3 py-3 font-semibold text-neutral-700 dark:text-neutral-300 border-b-2 border-neutral-200 dark:border-neutral-700">Type</th>
                  <th className="text-left px-3 py-3 font-semibold text-neutral-700 dark:text-neutral-300 border-b-2 border-neutral-200 dark:border-neutral-700">Triggered By</th>
                  <th className="text-left px-3 py-3 font-semibold text-neutral-700 dark:text-neutral-300 border-b-2 border-neutral-200 dark:border-neutral-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedHistory.map((test) => (
                  <tr
                    key={test.testId}
                    className={`
                      cursor-pointer transition-colors duration-150
                      hover:bg-neutral-50 dark:hover:bg-neutral-800/50
                      border-l-[3px] ${test.testResults.success ? 'border-l-success-500' : 'border-l-danger-500'}
                    `}
                    onClick={() => setSelectedTest(test)}
                  >
                    <td className="px-3 py-3 border-b border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300">
                      {formatDateTime(test.createdAt, activeTimezone(), 'full')}
                    </td>
                    <td className="px-3 py-3 border-b border-neutral-200 dark:border-neutral-700">
                      <StatusIndicator
                        status={getStatusColor(test.testResults.success)}
                        label={test.testResults.success ? 'Pass' : 'Fail'}
                        size="small"
                      />
                    </td>
                    <td className="px-3 py-3 border-b border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300">
                      {test.testResults.responseTime !== undefined
                        ? `${Math.round(test.testResults.responseTime)}ms`
                        : '-'}
                    </td>
                    <td className="px-3 py-3 border-b border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300">
                      {getTestTypeLabel(test.testType)}
                    </td>
                    <td className="px-3 py-3 border-b border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300">
                      {getTriggeredByLabel(test.triggeredBy)}
                    </td>
                    <td className="px-3 py-3 border-b border-neutral-200 dark:border-neutral-700">
                      <button
                        className="text-primary-600 dark:text-primary-400 hover:underline text-sm bg-transparent border-none cursor-pointer p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTest(test);
                        }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
                className="px-3 py-1.5 text-sm rounded border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="px-3 py-1.5 text-sm rounded border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <span className="px-4 text-sm text-neutral-600 dark:text-neutral-400">
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                className="px-3 py-1.5 text-sm rounded border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(totalPages)}
                className="px-3 py-1.5 text-sm rounded border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedTest && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[1001]"
          onClick={() => setSelectedTest(null)}
        >
          <div
            className="bg-white dark:bg-neutral-900 rounded-lg w-full max-w-[600px] max-h-[80vh] overflow-auto shadow-xl border border-neutral-200 dark:border-neutral-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-5 py-4 border-b border-neutral-200 dark:border-neutral-700">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Test Details</h3>
              <button
                className="text-2xl text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 bg-transparent border-none cursor-pointer p-0 leading-none"
                onClick={() => setSelectedTest(null)}
              >
                &times;
              </button>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-3 mb-5 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-md">
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] uppercase text-neutral-500 dark:text-neutral-400">Date/Time:</label>
                  <span className="text-sm text-neutral-800 dark:text-neutral-200">{formatDateTime(selectedTest.createdAt, activeTimezone(), 'full')}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] uppercase text-neutral-500 dark:text-neutral-400">Test Type:</label>
                  <span className="text-sm text-neutral-800 dark:text-neutral-200">{getTestTypeLabel(selectedTest.testType)}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] uppercase text-neutral-500 dark:text-neutral-400">Triggered By:</label>
                  <span className="text-sm text-neutral-800 dark:text-neutral-200">{getTriggeredByLabel(selectedTest.triggeredBy)}</span>
                </div>
                {selectedTest.userId && (
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] uppercase text-neutral-500 dark:text-neutral-400">User:</label>
                    <span className="text-sm text-neutral-800 dark:text-neutral-200">{selectedTest.userId}</span>
                  </div>
                )}
              </div>

              <TestResultPanel
                result={selectedTest.testResults}
                showStages={true}
                compact={false}
                connectionType={connectionType}
                transferMethod={transferMethod}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TestHistoryTable;
